import { getAuthRoute } from '~/common/utils/auth-routes'
import { isSharedPath } from '~/data/paths'
import { syncManager, type OrganizationChange } from './organization-sync-manager'

/**
 * Signing in to one of several organizations (OS-741).
 *
 * The session is scoped to one organization. This browser remembers the last one it signed in to,
 * so a person in several organizations lands there again without picking. On a new device, or if
 * that organization is no longer available, they pick one on the /auth/select-organization page.
 */

const LAST_ORGANIZATION_STORAGE_KEY = 'doss_last_organization_id'

export type SelectableOrganization = {
  id: string
  name: string
  /** A signed URL for the organization's logo, or null when it has none */
  logoUrl: string | null
}

type OrganizationSelectionRequiredResponse = {
  organizationSelectionRequired: true
  organizations: SelectableOrganization[]
}

/** Whether a sign-in response is asking which organization to sign in to */
export const isOrganizationSelectionRequired = (
  data: unknown,
): data is OrganizationSelectionRequiredResponse =>
  typeof data === 'object' &&
  data !== null &&
  (data as { organizationSelectionRequired?: unknown }).organizationSelectionRequired === true

/** The organization this browser last signed in to, or null (also when storage is unavailable) */
export const getLastOrganizationId = (): string | null => {
  try {
    return localStorage.getItem(LAST_ORGANIZATION_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Remembers the organization this browser signed in to */
export const setLastOrganizationId = (organizationId: string): void => {
  try {
    localStorage.setItem(LAST_ORGANIZATION_STORAGE_KEY, organizationId)
  } catch {
    // Storage unavailable (private mode, blocked site data): the picker is shown next time
  }
}

/**
 * Where a tab goes when another tab moves the session to a different organization.
 * - Sign-in pages, shared pages and the OAuth callback stay as they are: they don't depend on the
 *   session's organization, and reloading them would lose what was typed (or a one-time code).
 * - The CLI consent page reloads in place: its URL isn't tied to an organization, and it then offers to
 *   switch to the one the request is for, instead of losing the CLI sign-in.
 * - A tab that hasn't shown anything yet (`keepUrl`) reloads in place, keeping the link it was opened on.
 * - Other app pages go to the home page: their URL, cache and open queries belong to the previous
 *   organization.
 */
export const reloadForOrganizationChange = (
  location: Pick<Location, 'pathname' | 'assign' | 'reload'> = window.location,
  { keepUrl }: OrganizationChange = { keepUrl: false },
): void => {
  const { pathname } = location
  if (pathname.startsWith('/auth/') || pathname === '/oauth/callback' || isSharedPath(pathname)) {
    return
  }
  if (keepUrl || pathname === '/oauth/authorize') {
    location.reload()
    return
  }
  location.assign('/')
}

/**
 * Records which organization this tab's session is in, without telling other tabs or changing the
 * last-used organization. For restoring a session on page load: a check-session response reports
 * the cookie as it was when the request was sent, so if another tab switched meanwhile, announcing
 * it would send the other tabs (including the one that just switched) back to the old organization.
 */
export const rememberSessionOrganization = (organizationId: string): void => {
  syncManager.rememberSessionOrganization(organizationId)
}

/**
 * Records that the session just moved to this organization (a sign-in, the picker, the switcher),
 * remembers it for the next sign-in, and tells this browser's other tabs.
 *
 * Every tab shares the one session cookie, so after a sign-in or switch the other tabs are signed
 * in to this organization too, while still showing (and polling) pages from the previous one,
 * which the API refuses as belonging to another organization.
 */
export const setSessionOrganization = (organizationId: string): void => {
  syncManager.setSessionOrganization(organizationId)
}

/**
 * Listens for another tab signing the session in to a different organization, and then calls
 * `onChange` (by default `reloadForOrganizationChange`). A tab that doesn't know its organization
 * yet keeps the announcement until its check-session answers (see `rememberSessionOrganization`).
 * Returns a function that stops listening.
 */
export const listenForOrganizationChanges = (
  onChange?: (change: OrganizationChange) => void,
): (() => void) => {
  return syncManager.listenForOrganizationChanges(onChange)
}

/**
 * The organizations whose name contains `query`, ignoring case and surrounding spaces.
 * Sorted by name, the order the picker and the switcher both list them in.
 */
export const filterOrganizationsByName = <T extends { name: string }>(
  organizations: readonly T[],
  query: string,
): T[] => {
  const needle = query.trim().toLocaleLowerCase()
  return organizations
    .filter((organization) => organization.name.toLocaleLowerCase().includes(needle))
    .toSorted((a, b) => a.name.localeCompare(b.name))
}

const SAME_SITE_BASE = 'https://same-site.invalid'

/**
 * Only same-site paths, so a crafted link can't send someone elsewhere after signing in.
 * The path is resolved the way the browser will, so anything it would read as another host
 * (`//evil.com`, `/\evil.com`, a tab or newline between the slashes) falls back to `/`.
 */
export const getSafeReturnPath = (path: string | null | undefined): string => {
  if (typeof path !== 'string' || !path.startsWith('/')) {
    return '/'
  }
  try {
    const url = new URL(path, SAME_SITE_BASE)
    if (url.origin !== SAME_SITE_BASE) {
      return '/'
    }
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

/**
 * Finishes a pending sign-in with the organization the user picked.
 * Resolves with the sign-in response.
 */
export const selectOrganization = async <T>(organizationId: string): Promise<T> => {
  const response = await fetch(getAuthRoute('select-organization'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ organizationId }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || 'Failed to sign in')
  }
  const data: T = await response.json()
  setSessionOrganization(organizationId)
  return data
}

/**
 * Handles a sign-in that needs an organization picked: sends the user to the picker page, which
 * preselects the organization this browser last used and signs in once they confirm.
 * Resolves with null, since the page is navigating away.
 */
export const continueOrganizationSelection = (returnPath?: string | null): Promise<null> => {
  const pickerUrl = new URL('/auth/select-organization', window.location.origin)
  pickerUrl.searchParams.set('state', getSafeReturnPath(returnPath))
  window.location.assign(pickerUrl.toString())
  return Promise.resolve(null)
}

/**
 * Switches the current session to another organization the user belongs to, then reloads the app
 * so everything loads for that organization: at `returnPath` (a same-site path), or the home page.
 */
export const switchOrganization = async (
  organizationId: string,
  returnPath?: string,
): Promise<void> => {
  const response = await fetch(getAuthRoute('switch-organization'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ organizationId }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || 'Failed to switch organization')
  }
  setSessionOrganization(organizationId)
  window.location.assign(getSafeReturnPath(returnPath))
}
